/* useBrokerageGeocode — one-brokerage "put it on the map" action shared by
 * Sync addresses, the edit dialog (geocode right after saving a new
 * address) and the "Not on map — Fix here" pill. Persists via
 * POST /brokerages/{id}/save-place; rows that already have an address keep
 * their text and only gain coordinates.
 *
 * Returns { saved, notFound, fatal, error }:
 *   saved    → doc with coordinates
 *   notFound → Google answered but has no match (status "failed" persisted)
 *   fatal    → Maps itself is unusable (key rejected, timeout, not loaded);
 *              nothing persisted, bulk callers should stop. */
import { useCallback, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { findPlace, geocodeQueryFor, INFRA_ERRORS } from "@/lib/brokerageGeocode";
import { mapsAuthExplanation } from "@/lib/gmapsAuth";

function describeInfraError(code) {
  if (code === "MAPS_AUTH_FAILED") return mapsAuthExplanation();
  if (code === "TIMEOUT") return "Google didn't answer within 12 seconds. Check your connection and retry.";
  return "Google Maps hasn't finished loading in this browser. Reload the page and try again.";
}

export default function useBrokerageGeocode({ gmapsLoaded, hostRef, locationNameFor, onSaved }) {
  const [busyId, setBusyId] = useState(null);

  const geocodeOne = useCallback(
    async (b, { silent = false } = {}) => {
      if (!gmapsLoaded) {
        if (!silent) toast.error("Google Maps not ready yet — try again in a moment.");
        return { fatal: true, error: "MAPS_NOT_LOADED" };
      }
      setBusyId(b.id);
      const keepAddress = !!(b.office_address || "").trim();
      const query = geocodeQueryFor(b, locationNameFor ? locationNameFor(b) : "");
      try {
        const found = await findPlace(query, hostRef && hostRef.current);
        if (found.error && INFRA_ERRORS.includes(found.error)) {
          if (!silent) toast.error("Couldn't reach Google Maps", { description: describeInfraError(found.error), duration: 12000 });
          return { fatal: true, error: found.error };
        }
        const ok = !found.error;
        const { data } = await api.post(`/brokerages/${b.id}/save-place`, {
          office_address: ok ? found.formatted_address : "",
          lat: ok ? found.lat : null,
          lng: ok ? found.lng : null,
          place_id: ok ? found.place_id : "",
          geocode_status: ok ? "ok" : "failed",
          keep_address: keepAddress,
        });
        if (onSaved) onSaved(data);
        if (!silent) {
          if (ok) {
            toast.success(`${b.name} is on the map`, { description: found.formatted_address || query });
          } else {
            toast.error(`Google couldn't find "${query}"`, {
              description: `${found.error} — correct the address and save to try again.`,
            });
          }
        }
        return ok ? { saved: data } : { notFound: true, error: found.error };
      } catch (e) {
        if (!silent) toast.error("Couldn't save the pin", { description: formatApiError(e) });
        return { fatal: true, error: formatApiError(e) };
      } finally {
        setBusyId(null);
      }
    },
    [gmapsLoaded, hostRef, locationNameFor, onSaved],
  );

  return { geocodeOne, busyId };
}
